/**
 * Activity Logger Utility
 * Logs student activities to the backend for CMS tracking and Zoho sync
 */

const API_URL = window.location.origin;

/**
 * Log an activity to the backend
 * @param {Object} params - Activity parameters
 * @param {string} params.userId - The user ID (from auth context)
 * @param {string} params.activityType - Type of activity (see ACTIVITY_TYPES in backend)
 * @param {string} params.title - Human-readable title for the activity
 * @param {string} [params.description] - Optional description with more details
 * @param {Object} [params.metadata] - Optional metadata object with additional info
 * @param {string} [params.relatedEntity] - Optional ID of related entity (university slug, application ID, etc.)
 * @param {string} [params.relatedEntityType] - Optional type of related entity
 */
export const logActivity = async ({
  userId,
  activityType,
  title,
  description = null,
  metadata = null,
  relatedEntity = null,
  relatedEntityType = null
}) => {
  // Skip if no user ID (not logged in)
  if (!userId) {
    console.log('[ActivityLogger] Skipping - no user ID');
    return null;
  }

  try {
    const response = await fetch(`${API_URL}/api/activity/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        activity_type: activityType,
        title,
        description,
        metadata,
        related_entity: relatedEntity,
        related_entity_type: relatedEntityType
      })
    });

    if (!response.ok) {
      console.error('[ActivityLogger] Failed to log activity:', response.status);
      return null;
    }

    const data = await response.json();
    console.log('[ActivityLogger] Activity logged:', activityType, data.activity_id);
    return data.activity_id;
  } catch (error) {
    // Fail silently - activity logging should not break the main flow
    console.error('[ActivityLogger] Error logging activity:', error);
    return null;
  }
};

// ============ ACTIVITY TYPE CONSTANTS ============
// These match the backend ACTIVITY_TYPES

export const ACTIVITY_TYPES = {
  // AI University Finder
  AI_FINDER_SEARCH: 'ai_finder_search',
  AI_FINDER_RESULTS: 'ai_finder_results',
  AI_FINDER_SELECTION: 'ai_finder_selection',
  AI_FINDER_APPLY: 'ai_finder_apply',

  // Calculator Tools
  LOAN_CALCULATOR: 'loan_calculator_used',
  GPA_CALCULATOR: 'gpa_calculated',
  COST_CALCULATOR: 'cost_calculator_used',

  // Search & Engagement
  SEARCH_PERFORMED: 'search_performed',
  SEARCH_SAVED: 'search_saved',
  UNIVERSITY_SAVED: 'university_saved',
  UNIVERSITY_VIEWED: 'university_viewed',
  PROGRAM_VIEWED: 'program_viewed',

  // Content Generation
  SOP_GENERATED: 'sop_generated',
  LOR_GENERATED: 'lor_generated',

  // Documents
  DOCUMENT_UPLOADED: 'document_uploaded',

  // Applications
  APPLICATION_CREATED: 'application_created',

  // Communication
  COUNSELOR_CHAT: 'counselor_chat',
  CALLBACK_REQUESTED: 'callback_requested',

  // Profile
  PROFILE_UPDATED: 'profile_updated',
  LOGIN: 'login',
  PORTAL_REGISTERED: 'portal_registered'
};

// ============ CONVENIENCE FUNCTIONS ============

/**
 * Log an AI Finder search activity
 */
export const logAIFinderSearch = (userId, searchCriteria) => {
  return logActivity({
    userId,
    activityType: ACTIVITY_TYPES.AI_FINDER_SEARCH,
    title: `AI University Search: ${searchCriteria.targetDegree || 'degree'} in ${searchCriteria.countries?.join(', ') || 'multiple countries'}`,
    description: `Searched for ${searchCriteria.majors?.join(', ') || 'programs'}`,
    metadata: {
      target_degree: searchCriteria.targetDegree,
      countries: searchCriteria.countries,
      majors: searchCriteria.majors,
      budget_range: searchCriteria.budgetRange,
      test_scores: searchCriteria.testScores
    }
  });
};

/**
 * Log AI Finder results received
 */
export const logAIFinderResults = (userId, resultCount, persona) => {
  return logActivity({
    userId,
    activityType: ACTIVITY_TYPES.AI_FINDER_RESULTS,
    title: `Received ${resultCount} university recommendations`,
    description: `Student persona identified as: ${persona || 'standard'}`,
    metadata: {
      result_count: resultCount,
      persona
    }
  });
};

/**
 * Log university selection from AI Finder
 */
export const logAIFinderSelection = (userId, university, action) => {
  return logActivity({
    userId,
    activityType: ACTIVITY_TYPES.AI_FINDER_SELECTION,
    title: `${action === 'save' ? 'Saved' : 'Selected'} ${university.name}`,
    description: `${university.program || 'Program'} at ${university.location || university.country}`,
    metadata: {
      university_name: university.name,
      university_id: university.id,
      program: university.program,
      country: university.country,
      action
    },
    relatedEntity: university.id || university.slug,
    relatedEntityType: 'university'
  });
};

/**
 * Log apply intent from AI Finder
 */
export const logAIFinderApply = (userId, university) => {
  return logActivity({
    userId,
    activityType: ACTIVITY_TYPES.AI_FINDER_APPLY,
    title: `Started application for ${university.name}`,
    description: `Expressed intent to apply for ${university.program || 'program'}`,
    metadata: {
      university_name: university.name,
      university_id: university.id,
      program: university.program
    },
    relatedEntity: university.id || university.slug,
    relatedEntityType: 'university'
  });
};

/**
 * Log calculator usage
 */
export const logCalculatorUsed = (userId, calculatorType, inputs, results) => {
  const typeMap = {
    loan: ACTIVITY_TYPES.LOAN_CALCULATOR,
    gpa: ACTIVITY_TYPES.GPA_CALCULATOR,
    cost: ACTIVITY_TYPES.COST_CALCULATOR
  };

  const titles = {
    loan: `Used Loan Calculator: ${inputs.loanAmount || 'N/A'} loan`,
    gpa: `Calculated GPA: ${results.gpa || 'N/A'} on ${inputs.scale || 4.0} scale`,
    cost: `Used Cost Calculator for ${inputs.country || 'study abroad'}`
  };

  return logActivity({
    userId,
    activityType: typeMap[calculatorType] || ACTIVITY_TYPES.COST_CALCULATOR,
    title: titles[calculatorType] || `Used ${calculatorType} calculator`,
    metadata: { inputs, results }
  });
};

/**
 * Log university viewed
 */
export const logUniversityViewed = (userId, university) => {
  return logActivity({
    userId,
    activityType: ACTIVITY_TYPES.UNIVERSITY_VIEWED,
    title: `Viewed ${university.name}`,
    description: university.country,
    metadata: {
      university_name: university.name,
      university_id: university.id,
      country: university.country
    },
    relatedEntity: university.id || university.slug,
    relatedEntityType: 'university'
  });
};

/**
 * Log university saved/bookmarked
 */
export const logUniversitySaved = (userId, university) => {
  return logActivity({
    userId,
    activityType: ACTIVITY_TYPES.UNIVERSITY_SAVED,
    title: `Bookmarked ${university.name}`,
    metadata: {
      university_name: university.name,
      university_id: university.id
    },
    relatedEntity: university.id || university.slug,
    relatedEntityType: 'university'
  });
};

/**
 * Log search saved
 */
export const logSearchSaved = (userId, searchName, criteria) => {
  return logActivity({
    userId,
    activityType: ACTIVITY_TYPES.SEARCH_SAVED,
    title: `Saved search: ${searchName || 'My Search'}`,
    metadata: { search_name: searchName, criteria }
  });
};

/**
 * Log SOP generated
 */
export const logSOPGenerated = (userId, university) => {
  return logActivity({
    userId,
    activityType: ACTIVITY_TYPES.SOP_GENERATED,
    title: `Generated SOP for ${university?.name || 'application'}`,
    metadata: { university_name: university?.name },
    relatedEntity: university?.id,
    relatedEntityType: 'university'
  });
};

/**
 * Log document uploaded
 */
export const logDocumentUploaded = (userId, documentType, fileName) => {
  return logActivity({
    userId,
    activityType: ACTIVITY_TYPES.DOCUMENT_UPLOADED,
    title: `Uploaded ${documentType}`,
    description: fileName,
    metadata: { document_type: documentType, file_name: fileName }
  });
};

/**
 * Log callback requested
 */
export const logCallbackRequested = (userId, reason) => {
  return logActivity({
    userId,
    activityType: ACTIVITY_TYPES.CALLBACK_REQUESTED,
    title: 'Requested callback from counselor',
    description: reason,
    metadata: { reason }
  });
};

export default {
  logActivity,
  logAIFinderSearch,
  logAIFinderResults,
  logAIFinderSelection,
  logAIFinderApply,
  logCalculatorUsed,
  logUniversityViewed,
  logUniversitySaved,
  logSearchSaved,
  logSOPGenerated,
  logDocumentUploaded,
  logCallbackRequested,
  ACTIVITY_TYPES
};
